import { parseAmllLyrics, type AmllLyricLine } from '../amll-lyrics.ts';
import { parseTimedLyrics } from '../lyrics.ts';
import type { OnlineLyricDocument } from './online-lyrics.ts';

const laneLines = (document?: OnlineLyricDocument | null) => document
  ? parseAmllLyrics(document.fileName, document.text, parseTimedLyrics(document.fileName, document.text)) : [];

// QQ uses // for untranslated credits. It is an empty lane, not a lyric.
export const cleanAuxiliaryLyric = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.split(/\r?\n/).map(text => text.trim()).filter(text => text && !/^[\/／\\\s]+$/.test(text)).join('\n');
};

/** Provider lanes are matched only to their actual timestamp, including QRC /
 * YRC rounding. Unmatched text is never assigned a fabricated line position. */
export function mergeLyricLayers(original: AmllLyricLine[], translation?: OnlineLyricDocument | null, roman?: OnlineLyricDocument | null) {
  const translated = laneLines(translation);
  const romanized = laneLines(roman);
  const value = (lines: AmllLyricLine[], time: number) => {
    const matches = lines.filter(line => Math.abs(line.startTime - time) <= 100);
    if (!matches.length) return '';
    const nearest = Math.min(...matches.map(line => Math.abs(line.startTime - time)));
    return matches.filter(line => Math.abs(line.startTime - time) === nearest)
      .map(line => cleanAuxiliaryLyric(line.words.map(word => word.word).join(''))).filter(Boolean).join('\n');
  };
  return original.map(line => ({ ...line,
    translatedLyric: cleanAuxiliaryLyric(line.translatedLyric) || value(translated, line.startTime),
    romanLyric: cleanAuxiliaryLyric(line.romanLyric) || value(romanized, line.startTime),
  }));
}

export const lyricOriginalSignature = (lines: AmllLyricLine[]) => JSON.stringify(lines.map(line => [
  line.startTime, line.endTime, line.words.map(word => [word.startTime, word.endTime, word.word]),
]));

export function applyGeneratedTranslation(original: AmllLyricLine[], response: unknown): AmllLyricLine[] {
  const payload = response as { lines?: unknown; same?: unknown } | null;
  const input = original.filter(line => line.words.some(word => word.word.trim()));
  if (payload?.same === true) throw new Error('原词已经是目标语言，未生成新的译文');
  if (!Array.isArray(payload?.lines) || payload.lines.length !== input.length || payload.lines.some(line => typeof line !== 'string' || !line.trim())) {
    throw new Error('翻译返回的行数或内容不完整，原词保持不变');
  }
  let index = 0;
  return original.map(line => {
    if (!input.includes(line)) return line;
    const translatedLyric = cleanAuxiliaryLyric((payload.lines as string[])[index++]);
    return { ...line, translatedLyric: cleanAuxiliaryLyric(line.translatedLyric) || translatedLyric };
  });
}
